'use client';

import { produce } from 'immer';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { createParser, useQueryState } from 'nuqs';
import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useTransition,
} from 'react';
import slugify from 'react-slugify';

import { BrandWithCounts } from '@/services/filters/algolia/mappers';
import { makeKiaFirst } from '@/services/filters/filters.service';

import { isFilterEqual, useQueryNonce, useResetPage } from '../../filters/new-filters/utils';

import styles from './make-model-search-filter.module.scss';

export interface MakeModelSearchFilterProps {
  brandsAndRanges: BrandWithCounts[];
  defaultMake?: string;
  defaultModel?: string;
}

const normalize = (v: string) =>
  v.toLowerCase().replace(/\s+/g, ' ').trim();

const brandAliases: Record<string, string[]> = {
  volkswagen: ['vw'],
  'mercedes-benz': ['mercedes', 'benz'],
};

const matchesSearch = (
  text: string,
  searchQuery: string,
): boolean => {
  if (!searchQuery.trim()) return true;

  const normalizedText = normalize(text);

  const searchWords = normalize(searchQuery)
    .split(' ')
    .filter(Boolean);

  return searchWords.every((word) => {
    if (normalizedText.includes(word)) {
      return true;
    }

    return Object.entries(brandAliases).some(
      ([brand, aliases]) =>
        aliases.includes(word) &&
        normalizedText.includes(brand),
    );
  });
};

const matchesSearchContext = (
  searchQuery: string,
  ...texts: string[]
): boolean => {
  if (!searchQuery.trim()) return true;

  const combinedText = normalize(
    texts.filter(Boolean).join(' '),
  );

  const searchWords = normalize(searchQuery)
    .split(' ')
    .filter(Boolean);

  return searchWords.every((word) =>
    combinedText.includes(word),
  );
};

export const MakeModelSearchFilter: React.FC<MakeModelSearchFilterProps> = ({
  brandsAndRanges,
  defaultMake,
  defaultModel,
}) => {
  const { generate } = useQueryNonce();
  const resetPage = useResetPage();
  const [, startTransition] = useTransition();
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const inputClickedRef = useRef(false);
  const isLandingDefaultRef = useRef(
    Boolean(defaultMake || defaultModel),
  );
  const cleanupRunRef = useRef(false);
  const chipsContainerRef = useRef<HTMLDivElement>(null);

  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const disableLandingDefaults = () => {
    if (isLandingDefaultRef.current) {
      isLandingDefaultRef.current = false;
    }
  };

  const isLandingBrand = (brandName: string) => defaultMake &&
    slugify(brandName) === slugify(defaultMake);
  const isLandingModel = (brandName: string, modelName?: string) =>
    defaultMake &&
    defaultModel &&
    slugify(brandName) === slugify(defaultMake) &&
    modelName &&
    slugify(modelName) === slugify(defaultModel);

  const baseData = useMemo(() => {
    return brandsAndRanges
      .filter((brand) => brand.vehicleCount > 0)
      .map((brand) => {
        const isDefaultBrand =
          isLandingDefaultRef.current &&
          Boolean(defaultMake) &&
          slugify(brand.name) === slugify(defaultMake!);

        const children = brand.range_ids
          .filter((range) => range.vehicleCount > 0)
          .map((range) => {
            const isDefaultRange =
              isLandingDefaultRef.current &&
              isDefaultBrand &&
              Boolean(defaultModel) && slugify(range.name) === slugify(defaultModel);

            const models = (range.models ?? []).map(
              (model) => ({
                id: model.id,
                name: model.name,
                vehicleCount: model.vehicleCount,
                isChecked: false,
              }),
            );

            return {
              id: range.id,
              name: range.name,
              isChecked: Boolean(isDefaultRange),
              vehicleCount: range.vehicleCount,
              models,
            };
          });

        return {
          id: brand.id,
          name: brand.name,
          isChecked: Boolean(isDefaultBrand),
          vehicleCount: brand.vehicleCount,
          children,
        };
      });
  }, [
    brandsAndRanges,
    defaultMake,
    defaultModel,
  ]);

  const parseBrandRanges = createParser({
    parse(queryValue) {
      if (!queryValue) {
        return baseData;
      }

      const tokens = queryValue
        .split(',')
        .map(normalize)
        .filter(Boolean);

      const modelTokens = tokens.map(
        (token) => token.split('~')[0],
      );

      return baseData.map((brand) => {
        const brandKey = normalize(brand.name);
        const isBrandSelected = tokens.includes(brandKey);

        const children = brand.children.map((range) => {
          const rangeKey = `${brandKey}-${normalize(range.name)}`;

          const isRangeSelected = tokens.includes(rangeKey);

          const models = range.models.map((model) => {
            const modelToken = `${rangeKey}-${normalize(model.id)}`;
            const isModelSelected =  modelTokens.includes(modelToken);

            return {
              ...model,
              isChecked: isModelSelected,
            };
          });

          const hasSelectedModel = models.some((model) => model.isChecked);

          return {
            ...range,
            models,
            isChecked: isRangeSelected || hasSelectedModel,
          };
        });

        const hasSelectedRange = children.some((range) => range.isChecked);

        return {
          ...brand,
          children,
          isChecked:
            isBrandSelected ||
            hasSelectedRange,
        };
      });
    },

    serialize(value) {
      const list: string[] = [];

      value.forEach((brand) => {
        const hasSelectedChildren = brand.children.some(
          (range) =>
            range.isChecked ||
            range.models.some(
              (model) => model.isChecked,
            ),
        );

        const allRangesSelected =
          brand.children.length > 0 &&
          brand.children.every(
            (range) => range.isChecked,
          );

        if (brand.isChecked && (!hasSelectedChildren || allRangesSelected)) {
          list.push(brand.name);
          return;
        }

        brand.children.forEach((range) => {
          const selectedModels =
            range.models.filter(
              (model) => model.isChecked,
            );

          const allModelsSelected =
            range.models.length > 0 &&
            range.models.every(
              (model) => model.isChecked,
            );

          if (range.isChecked && (selectedModels.length === 0 || allModelsSelected)) {
            list.push(
              `${brand.name}-${range.name}`,
            );
            return;
          }

          selectedModels.forEach((model) => {
            list.push(
              `${brand.name}-${range.name}-${model.id}~${model.name}`,
            );
          });
        });
      });

      return list.length > 0
        ? list.join(',')
        : '';
    },

    eq: isFilterEqual,
  });

  const [queryState, setQueryState] = useQueryState(
    'makemodel',
    parseBrandRanges
      .withDefault(baseData)
      .withOptions({
        startTransition,
        shallow: false,
        history: 'push',
      }),
  );

  /**
   * Close dropdown when clicking outside.
   */
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!containerRef.current) return;

      if (!containerRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
        inputClickedRef.current = false;
        inputRef.current?.blur();
      }
    };

    document.addEventListener('mousedown',handleClickOutside);

    return () =>
      document.removeEventListener(
        'mousedown',
        handleClickOutside,
      );
  }, []);

  /**
   * Preserve existing landing-page URL cleanup.
   */
  useEffect(() => {
    if ((defaultMake || defaultModel) && !cleanupRunRef.current) {
      const params = new URLSearchParams(
        searchParams.toString(),
      );

      if (params.has('makemodel')) {
        params.delete('makemodel');
        const newUrl = `${pathname}${params.toString() ? '?' + params.toString() : ''}`;

        router.replace(newUrl);
      }

      cleanupRunRef.current = true;
    }
  }, []);

  /**
   * Remove empty makemodel parameter.
   */
  useEffect(() => {
    if (!defaultMake && !defaultModel) {
      const hasAnySelected =
        queryState?.some(
          (brand) =>
            brand.isChecked ||
            brand.children?.some(
              (range) =>
                range.isChecked ||
                range.models?.some(
                  (model) => model.isChecked,
                ),
            ),
        );

      const hasMakemodelParam = searchParams.has('makemodel');

      if (!hasAnySelected && hasMakemodelParam) {
        const params = new URLSearchParams(
          searchParams.toString(),
        );

        params.delete('makemodel');

        const newUrl = `${pathname}${
          params.toString()
            ? '?' + params.toString()
            : ''
        }`;

        router.replace(newUrl);
      }
    }
  }, [
    queryState,
    searchParams,
    pathname,
    router,
    defaultMake,
    defaultModel,
  ]);

  const enableUrlUpdates = () => {
    if (isLandingDefaultRef.current) {
      isLandingDefaultRef.current = false;
    }
  };

  const updateChipScrollState = () => {
    const element = chipsContainerRef.current;

    if (!element) return;

    setCanScrollLeft(element.scrollLeft > 0);

    setCanScrollRight(
      element.scrollLeft + element.clientWidth <
        element.scrollWidth - 1,
    );
  };

  const scrollChips = (direction: 'left' | 'right') => {
    const element = chipsContainerRef.current;

    if (!element) return;

    element.scrollBy({
      left:
        direction === 'right'
          ? element.clientWidth * 0.7
          : -(element.clientWidth * 0.7),
      behavior: 'smooth',
    });
  };

  useEffect(() => {
    updateChipScrollState();

    const element = chipsContainerRef.current;

    if (!element) return;

    element.addEventListener(
      'scroll',
      updateChipScrollState,
    );

    const resizeObserver = new ResizeObserver(
      updateChipScrollState,
    );

    resizeObserver.observe(element);

    return () => {
      element.removeEventListener(
        'scroll',
        updateChipScrollState,
      );

      resizeObserver.disconnect();
    };
  }, [queryState]);

  /**
   * Toggle entire brand.
   *
   * Existing functionality preserved:
   * selecting a brand selects everything below it.
   */
  const toggleBrand = (brandName: string) => {
    resetPage();

    const isLandingPage = defaultMake || defaultModel;
    const removingLandingBrand = isLandingBrand(brandName);

    if (!isLandingPage || removingLandingBrand) {
      disableLandingDefaults();
    }

    if (!isLandingPage || removingLandingBrand) {
      enableUrlUpdates();
    }

    setQueryState((items) =>
      produce(items, (draft) => {
        const brand = draft.find(
          (item) => item.name === brandName,
        );

        if (!brand) return;

        const nextChecked = !brand.isChecked;

        brand.isChecked = nextChecked;

        // When deselecting a brand, clear its children.
        if (!nextChecked) {
          brand.children.forEach((range) => {
            range.isChecked = false;

            range.models.forEach((model) => {
              model.isChecked = false;
            });
          });
        }
      }),
    );

    setSearch('');
    generate();

    if (removingLandingBrand) {
      window.location.replace('/used-cars/search');
    }
  };
 
  const toggleRange = (
    brandName: string,
    rangeName: string,
  ) => {
    resetPage();

    const isLandingPage = Boolean(defaultMake || defaultModel);
    const removingLandingRange = isLandingModel(brandName,rangeName);

    if (!isLandingPage || removingLandingRange) {
      disableLandingDefaults();
      enableUrlUpdates();
    }

    setQueryState((items) =>
      produce(items, (draft) => {
        const brand = draft.find(
          (item) => item.name === brandName,
        );

        if (!brand) return;

        const range = brand.children.find(
          (item) => item.name === rangeName,
        );

        if (!range) return;

        const nextChecked = !range.isChecked;

        range.isChecked = nextChecked;

        /*
        * Selecting/deselecting a range must not
        * keep individual model selections.
        */
        range.models.forEach((model) => {
          model.isChecked = false;
        });

        brand.isChecked =
        brand.children.some(
          (item) =>
            item.isChecked ||
            item.models.some(
              (model) => model.isChecked,
            ),
        );
      }),
    );

    setSearch('');
    generate();

    if (removingLandingRange) {
      window.location.replace('/used-cars/search');
    }
  };

  const toggleModel = (
    brandName: string,
    rangeName: string,
    modelId: string,
  ) => {
    resetPage();

    const isLandingPage = Boolean(defaultMake || defaultModel);

    if (isLandingPage) {
      disableLandingDefaults();
    }

    enableUrlUpdates();

    const range = queryState
      ?.find((brand) => brand.name === brandName)
      ?.children.find(
        (range) => range.name === rangeName,
      );

    if (!range) return;

    setExpanded((prev) => ({
      ...prev,
      [brandName]: true,
      [`${brandName}-${range.id}`]: true,
    }));

    setQueryState((items) =>
      produce(items, (draft) => {
        const draftBrand = draft.find(
          (item) => item.name === brandName,
        );

        if (!draftBrand) return;

        const draftRange = draftBrand.children.find(
            (item) => item.name === rangeName,
          );

        if (!draftRange) return;

        const draftModel =
          draftRange.models.find(
            (item) => item.id === modelId,
          );

        if (!draftModel) return;

        draftModel.isChecked = !draftModel.isChecked;

        draftRange.isChecked =
          draftRange.models.some(
            (model) => model.isChecked,
          );

        draftBrand.isChecked =
          draftBrand.children.some(
            (range) =>
              range.isChecked ||
              range.models.some(
                (model) => model.isChecked,
              ),
          );
      }),
    );

    setSearch('');
    generate();
  };

  useEffect(() => {
    if (!search.trim()) {
      return;
    }

    const next: Record<string, boolean> = {};

    queryState?.forEach((brand) => {
      const brandMatch = matchesSearch(
        brand.name,
        search,
      );

      const matchingRanges =
        brand.children.filter((range) => {
          const rangeMatch =
            matchesSearchContext(
              search,
              brand.name,
              range.name,
            );

          const modelMatch =
            range.models.some((model) =>
              matchesSearchContext(
                search,
                brand.name,
                range.name,
                model.name,
              ),
            );

          return rangeMatch || modelMatch;
        });

      if (brandMatch || matchingRanges.length > 0) {
        next[brand.name] = true;
      }

      matchingRanges.forEach((range) => {
        next[
          `${brand.name}-${range.id}`
        ] = true;
      });
    });

    setExpanded(next);
  }, [search, queryState]);

  const sortedBrands = useMemo(() => {
    const result = makeKiaFirst(
      queryState || [],
    );

    return result;
  }, [queryState]);
 
  const hasSelection = queryState?.some(
    (brand) =>
      brand.isChecked ||
      brand.children.some(
        (range) =>
          range.isChecked ||
          range.models.some((model) => model.isChecked),
      )) ?? false;

  const showClearButton = search.length > 0 || hasSelection;

  const clearAllMakeModels = () => {
    resetPage();

    const isSearchPage = pathname.includes('/search');

    const isMakeModelLandingPage =
      pathname.startsWith('/used-cars/') &&
      !pathname.includes('/search') &&
      !pathname.includes('/provinces') &&
      !pathname.includes('/under-') &&
      !pathname.includes('/demo') &&
      !pathname.includes('-bodytype') &&
      pathname.split('/').length >= 3; 
    const hasMakeModelFilters = searchParams.has('makemodel');

    if (isSearchPage) {
      disableLandingDefaults();
      setSearch('');
      setExpanded({});
      setQueryState(baseData);
      generate();
      return;
    }

    if (
      hasMakeModelFilters &&
      !isMakeModelLandingPage
    ) {
      disableLandingDefaults();
      setSearch('');
      setExpanded({});
      setQueryState(baseData);
      generate();
      return;
    }

    if (isMakeModelLandingPage) {
      window.location.replace(
        '/used-cars/search',
      );
    }
  };

  const closeMakeModelDropdown = () => {
    setIsDropdownOpen(false);
    setSearch('');
    inputClickedRef.current = false;
    inputRef.current?.blur();
  };

  return (
    <div
      className={styles.searchContainer}
      ref={containerRef}
    >
      <div
        className={styles.searchInputWrapper}
      >
        {/* ---------------- CHIPS INSIDE SEARCH BOX ---------------- */}
        {queryState && hasSelection && (
          <div className={styles.chipsWrapper}>
            {canScrollLeft && (
              <button
                type="button"
                className={`${styles.chipScrollButton} ${styles.chipScrollButtonLeft}`}
                onClick={(e) => {
                  e.stopPropagation();
                  scrollChips('left');
                }}
                aria-label="Previous selections"
              >
                ←
              </button>
            )}
            <div
              ref={chipsContainerRef}
              className={styles.chipsInside}
            >
              {queryState.map((brand: any) => {
                  /*
                  * Show brand chip only when the brand itself
                  * is selected and nothing below it is selected.
                  */
                  const hasSelectedChildren =
                    brand.children.some(
                      (range: any) =>
                        range.isChecked ||
                        range.models.some(
                          (model: any) => model.isChecked,
                        ),
                    );

                  const allRangesSelected =
                    brand.children.length > 0 &&
                    brand.children.every(
                      (range: any) => range.isChecked,
                    );

                  if (brand.isChecked && (!hasSelectedChildren || allRangesSelected)) {
                    return (
                      <span
                        key={brand.name}
                        className={styles.chip}
                      >
                        {brand.name}

                        <button
                          onClick={() =>
                            toggleBrand(brand.name)
                          }
                        >
                          ×
                        </button>
                      </span>
                    );
                  }

                  return brand.children.flatMap(
                    (range: any) => {
                      const selectedModels =
                        range.models.filter(
                          (model: any) => model.isChecked,
                        );
                        const allModelsSelected =
                          range.models.length > 0 &&
                          range.models.every(
                            (model: any) => model.isChecked,
                          );

                      /*
                      * Range chip only when the range is selected
                      * without individual model selections.
                      */
                      if (range.isChecked && (selectedModels.length === 0 || allModelsSelected)
                      ) {
                        return (
                          <span
                            key={`${brand.name}-${range.name}`}
                            className={styles.chip}
                          >
                            {brand.name} {range.name}

                            <button
                              onClick={() =>
                                toggleRange(
                                  brand.name,
                                  range.name,
                                )
                              }
                            >
                              ×
                            </button>
                          </span>
                        );
                      }

                      /*
                      * Individual model chips.
                      */
                      return selectedModels.map(
                        (model: any) => (
                          <span
                            key={`${brand.name}-${range.name}-${model.id}`}
                            className={styles.chip}
                          >
                            {brand.name} {range.name}{' '}
                            {model.name}

                            <button
                              onClick={() =>
                                toggleModel(
                                  brand.name,
                                  range.name,
                                  model.id,
                                )
                              }
                            >
                              ×
                            </button>
                          </span>
                        ),
                      );
                    },
                  );
              })}
            </div>
            {canScrollRight && (
              <button
                type="button"
                className={`${styles.chipScrollButton} ${styles.chipScrollButtonRight}`}
                onClick={(e) => {
                  e.stopPropagation();
                  scrollChips('right');
                }}
                aria-label="Next selections"
              >
                →
              </button>
            )}
          </div>
        )}

        <input
          ref={inputRef}
          className={styles.searchInput}
          placeholder={
            queryState?.some(
              (brand) =>
                brand.isChecked ||
                brand.children.some(
                  (range) =>
                    range.isChecked ||
                    range.models.some(
                      (model) =>
                        model.isChecked,
                    ),
                ),
            )
              ? 'Add more...'
              : 'Search make or model'
          }
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setIsDropdownOpen(true);
          }}
          onMouseDown={() => {
            inputClickedRef.current = true;
            setIsDropdownOpen(true);
          }}
        />

        <div className={styles.filterActions}>
          {showClearButton && (
            <button
              type="button"
              className={styles.clearButton}
              onClick={(e) => {
                e.stopPropagation();
                clearAllMakeModels();
              }}
              aria-label="Clear make and model filters"
            >
              Clear
            </button>
          )}

          {isDropdownOpen && (
            <button
              type="button"
              className={styles.closeButton}
              onClick={(e) => {
                e.stopPropagation();
                closeMakeModelDropdown();
              }}
              aria-label="Close make and model filter"
            >
              Close
            </button>
          )}
        </div>
      </div>

      {isDropdownOpen && (
        <div
          className={styles.dropdownOverlay}
        >
          {search &&
          (() => {
            const hasAnyMatches =
              sortedBrands.some((brand) => {
                /*
                * Brand-only search.
                */
                const brandMatch =
                  matchesSearch(
                    brand.name,
                    search,
                  );

                if (brandMatch) {
                  return true;
                }

                /*
                * Range/model search using the
                * complete hierarchy context.
                */
                return brand.children.some((range) => {
                  const rangeMatch =
                    matchesSearchContext(
                      search,
                      brand.name,
                      range.name,
                    );

                  if (rangeMatch) {
                    return true;
                  }

                  return range.models.some((model) =>
                    matchesSearchContext(
                      search,
                      brand.name,
                      range.name,
                      model.name,
                    ),
                  );
                });
              });

            return !hasAnyMatches;
          })() ? (
            <div
              className={styles.noResults}
            >
              No makes or models match your
              search term
            </div>
          ) : (
            <div className={styles.list}>
              {sortedBrands.map(
                (brand) => {
                  const brandMatch = matchesSearch(brand.name,search);

                  const matchingRanges = brand.children.filter((range) => {
                      const rangeMatch = matchesSearchContext(
                        search,
                        brand.name,
                        range.name,
                      );
                     
                      const modelMatch = range.models.some(
                        (model) =>
                          matchesSearchContext(
                            search,
                            brand.name,
                            range.name,
                            model.name,
                          ),
                      );

                      return rangeMatch || modelMatch;
                    });

                  if (search && !brandMatch && matchingRanges.length === 0) {
                    return null;
                  }

                  const rangesToRender = 
                    search ? brandMatch ? brand.children : matchingRanges : brand.children;

                  const isExpanded = expanded[brand.name];

                  return (
                    <div key={brand.name}>
                      {/* BRAND */}
                      <div
                        className={
                          styles.brand
                        }
                        onClick={() => {
                          setExpanded(
                            (prev) => ({
                              ...prev,
                              [brand.name]:
                                !prev[
                                  brand.name
                                ],
                            }),
                          );
                        }}
                      >
                        <label
                          className={
                            styles.brandCheckbox
                          }
                          onClick={(e) =>
                            e.stopPropagation()
                          }
                        >
                          <input
                            type="checkbox"
                            checked={
                              brand.isChecked
                            }
                            onChange={() =>
                              toggleBrand(
                                brand.name,
                              )
                            }
                          />

                          <span>
                            {brand.name} (
                            {
                              brand.vehicleCount
                            }
                            )
                          </span>
                        </label>

                        {rangesToRender.length >
                          0 && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();

                              setExpanded(
                                (prev) => ({
                                  ...prev,
                                  [brand.name]:
                                    !prev[
                                      brand.name
                                    ],
                                }),
                              );
                            }}
                          >
                            {isExpanded
                              ? 'Hide Models'
                              : 'Show Models'}
                          </button>
                        )}
                      </div>

                      {/* RANGES */}
                      {isExpanded &&
                        rangesToRender.map((range) => {
                          const rangeMatch =
                            matchesSearchContext(
                              search,
                              brand.name,
                              range.name,
                            );

                          const matchingModels =
                            range.models.filter((model) =>
                              matchesSearchContext(
                                search,
                                brand.name,
                                range.name,
                                model.name,
                              ),
                            );

                          const modelsToRender =
                            search && !rangeMatch
                              ? matchingModels
                              : range.models;

                          const rangeKey = `${brand.name}-${range.id}`;

                          const isRangeExpanded =
                            expanded[rangeKey];

                          return (
                            <div
                              key={rangeKey}
                              className={styles.range}
                            >
                              {/* RANGE */}
                              <div 
                                className={styles.rangeRow}  
                                onClick={() => {
                                  setExpanded((prev) => ({
                                    ...prev,
                                    [rangeKey]: !prev[rangeKey],
                                  }));
                                }}>
                                <label
                                  className={styles.rangeCheckbox}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <input
                                    type="checkbox"
                                    checked={range.isChecked}
                                    onChange={() =>
                                      toggleRange(
                                        brand.name,
                                        range.name,
                                      )
                                    }
                                  />

                                  <span>
                                    {range.name} (
                                    {range.vehicleCount}
                                    )
                                  </span>
                                </label>

                                {/* SHOW / HIDE MODELS BUTTON */}
                                {range.models.length > 0 && (
                                  <button
                                    type="button"
                                    className={styles.rangeToggle}
                                    onClick={(e) => {
                                      e.stopPropagation();

                                      setExpanded((prev) => ({
                                        ...prev,
                                        [rangeKey]:
                                          !prev[rangeKey],
                                      }));
                                    }}
                                  >
                                    {isRangeExpanded
                                      ? 'Hide Variants'
                                      : 'Show Variants'}
                                  </button>
                                )}
                              </div>

                              {/* MODELS / DERIVATIVES */}
                              {isRangeExpanded &&
                                modelsToRender.map((model) => (
                                  <label
                                    key={`${brand.name}-${range.id}-${model.id}`}
                                    className={styles.model}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={model.isChecked}
                                      onChange={() =>
                                        toggleModel(
                                          brand.name,
                                          range.name,
                                          model.id,
                                        )
                                      }
                                    />

                                    <span>
                                      {model.name} (
                                      {model.vehicleCount}
                                      )
                                    </span>
                                  </label>
                                ))}
                            </div>
                          );
                      })}
                    </div>
                  );
                },
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
